import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { BbotButton, BbotModal, Input, InputNumber, Radio, notification } from "top-component-library";
import { SELF_DELIVERY_FIELD_CHOICES } from "./constants";
import { FormRow, StackedButtonGroup } from "./styles";

const INITIAL_FIELDS_TO_SHOW = { maxDeliveryMiles: false, allowedZipCodes: false };

// Mirrors add-self-delivery-dialog.html/addSelfDeliveryController: a linear wizard over
// intro (only shown when already enabled) -> chooseFields -> setConfig, with a
// disableWarning branch off intro. fieldsToShow is re-chosen on every edit; it isn't
// derived from which value is currently set, matching legacy's behavior.
const SelfDeliveryDialog = ({ visible, enabled, maxDeliveryMiles, allowedZipCodes, onClose, onSave }) => {
  const [step, setStep] = useState(enabled ? "intro" : "chooseFields");
  const [fieldsToShow, setFieldsToShow] = useState(INITIAL_FIELDS_TO_SHOW);
  const [maxDeliveryMilesInput, setMaxDeliveryMilesInput] = useState(maxDeliveryMiles || undefined);
  const [allowedZipCodesString, setAllowedZipCodesString] = useState(allowedZipCodes.join(", "));
  const [saving, setSaving] = useState(false);

  const resetToStart = () => {
    setStep(enabled ? "intro" : "chooseFields");
    setFieldsToShow(INITIAL_FIELDS_TO_SHOW);
    setMaxDeliveryMilesInput(maxDeliveryMiles || undefined);
    setAllowedZipCodesString(allowedZipCodes.join(", "));
  };

  useEffect(() => {
    if (visible) resetToStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const chooseFields = (choice) => {
    if (choice === "both") {
      setFieldsToShow({ maxDeliveryMiles: true, allowedZipCodes: true });
    } else {
      setFieldsToShow((current) => ({ ...current, [choice]: true }));
    }
    setStep("setConfig");
  };

  const handleConfirm = async () => {
    if (step === "disableWarning") {
      setSaving(true);
      const success = await onSave({ enabled: false, maxDeliveryMiles: null, allowedZipCodes: [] }, false);
      setSaving(false);
      if (success) onClose();
      return;
    }

    let zipCodes = [];
    if (fieldsToShow.allowedZipCodes && allowedZipCodesString.length) {
      zipCodes = allowedZipCodesString.split(",").map((zip) => zip.trim());
      if (zipCodes.some((zip) => zip.length !== 5)) {
        notification.error({
          message:
            "One of your zip codes was shorter or longer than 5 digits. Please ensure your zip codes are all 5 digits long.",
        });
        return;
      }
    }

    setSaving(true);
    const success = await onSave(
      {
        enabled: true,
        maxDeliveryMiles: fieldsToShow.maxDeliveryMiles ? maxDeliveryMilesInput : null,
        allowedZipCodes: fieldsToShow.allowedZipCodes ? zipCodes : [],
      },
      fieldsToShow.allowedZipCodes && !fieldsToShow.maxDeliveryMiles
    );
    setSaving(false);
    if (success) onClose();
  };

  const confirmDisabled =
    step === "setConfig" &&
    ((fieldsToShow.maxDeliveryMiles && !maxDeliveryMilesInput) ||
      (fieldsToShow.allowedZipCodes && !allowedZipCodesString.length));

  return (
    <BbotModal
      title="Delivery Zone"
      visible={visible}
      onCancel={onClose}
      footer={[
        <BbotButton key="start-over" onClick={resetToStart}>
          Start Over
        </BbotButton>,
        <BbotButton key="close" onClick={onClose}>
          Close
        </BbotButton>,
        (step === "setConfig" || step === "disableWarning") && (
          <BbotButton key="confirm" type="primary" loading={saving} disabled={confirmDisabled} onClick={handleConfirm}>
            Confirm
          </BbotButton>
        ),
      ]}
    >
      {step === "intro" && (
        <div>
          <h3>What would you like to do?</h3>
          <StackedButtonGroup>
            <BbotButton type="primary" onClick={() => setStep("chooseFields")}>
              Change my settings
            </BbotButton>
            <BbotButton type="primary" onClick={() => setStep("disableWarning")}>
              Disable delivery zone feature
            </BbotButton>
          </StackedButtonGroup>
        </div>
      )}
      {step === "chooseFields" && (
        <div>
          <h3>How would you like to check customer addresses?</h3>
          <Radio.Group onChange={(event) => chooseFields(event.target.value)}>
            <StackedButtonGroup>
              {SELF_DELIVERY_FIELD_CHOICES.map((choice) => (
                <Radio key={choice.value} value={choice.value}>
                  {choice.label}
                </Radio>
              ))}
            </StackedButtonGroup>
          </Radio.Group>
        </div>
      )}
      {step === "setConfig" && (
        <div>
          <h3>Set Delivery Zone Settings</h3>
          {fieldsToShow.maxDeliveryMiles && (
            <FormRow>
              <label htmlFor="max-delivery-miles">Maximum distance orders will be delivered to (in miles)</label>
              <InputNumber
                id="max-delivery-miles"
                min={0}
                max={1000}
                step={0.1}
                placeholder="5"
                value={maxDeliveryMilesInput}
                onChange={setMaxDeliveryMilesInput}
              />
            </FormRow>
          )}
          {fieldsToShow.allowedZipCodes && (
            <FormRow>
              <p>
                Please enter a comma-separated list of zip codes to deliver to. All zip codes must be exactly five
                digits.
              </p>
              <label htmlFor="allowed-zip-codes">Orders will only be delivered to the following zip codes</label>
              <Input.TextArea
                id="allowed-zip-codes"
                placeholder="10001, 10002, 10003"
                value={allowedZipCodesString}
                onChange={(event) => setAllowedZipCodesString(event.target.value)}
              />
            </FormRow>
          )}
        </div>
      )}
      {step === "disableWarning" && (
        <div>
          <h3>Disable delivery zone feature</h3>
          <p>
            Continuing will <strong>completely</strong> disable this feature and remove your saved settings. Orders will
            no longer be checked against delivery distance or allowed zip codes and{" "}
            <strong>you will have to check if orders can be delivered manually as they come in.</strong> If you change
            your mind, you can always re-enable the feature on the settings page.
          </p>
          <p>
            Press <strong>confirm</strong> to disable the feature, or start over to go back.
          </p>
        </div>
      )}
    </BbotModal>
  );
};

SelfDeliveryDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  enabled: PropTypes.bool.isRequired,
  maxDeliveryMiles: PropTypes.number,
  allowedZipCodes: PropTypes.arrayOf(PropTypes.string).isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

SelfDeliveryDialog.defaultProps = {
  maxDeliveryMiles: null,
};

export default SelfDeliveryDialog;
